import React, { useEffect, useState } from "react";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import appConfig from "../../appConfig";
import { getToken } from "../../services/apiService";
import { AGILE_SIGN_IN_REQUIRED } from "./AgileSignIn";

// Revision of the order to read from Agile. The list comes from Agile (GET agile/revisions) with the person's
// session; empty value = the revision Agile opens. order = the ESOI or work order of the row.
// onNeedSignIn(retry) opens the Agile sign-in and calls retry once signed in.
export default function AgileRevisionSelect({ id, order, value, onChange, signedIn, onNeedSignIn }) {
  const { instance, accounts } = useMsal();
  const [revisions, setRevisions] = useState(null);
  const [loadedFor, setLoadedFor] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Another order has other revisions
  const trimmedOrder = (order || "").trim();
  useEffect(() => {
    if (loadedFor && loadedFor !== trimmedOrder) {
      setRevisions(null);
      setLoadedFor("");
      setError("");
      onChange("");
    }
  }, [trimmedOrder, loadedFor, onChange]);

  // The API first checks with Agile that the session still works
  const fetchRevisions = async () => {
    setLoading(true);
    setError("");
    try {
      const accessToken = await getToken(instance, accounts);
      const response = await axios.get(`${appConfig.apiUrl}agile/revisions`, {
        params: { value: trimmedOrder },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const result = response.data;
      if (result?.status !== "OK") {
        if (result?.message === AGILE_SIGN_IN_REQUIRED) {
          setError("Your Agile session ended. Sign in again to load the versions.");
          onNeedSignIn(fetchRevisions);
          return;
        }
        setError(result?.message || "The revisions could not be read from Agile.");
        return;
      }
      setRevisions(result.data.revisions || []);
      setLoadedFor(trimmedOrder);
      if (!result.data.revisions?.length) setError(`Agile shows no revisions for ${result.data.soi}.`);
    } catch (err) {
      setError(err.response?.data?.message || "The revisions could not be read from Agile.");
    } finally {
      setLoading(false);
    }
  };

  const load = () => {
    if (!trimmedOrder) {
      setError("Enter the ESOI or the work order first.");
      return;
    }
    if (!signedIn) {
      onNeedSignIn(fetchRevisions);
      return;
    }
    fetchRevisions();
  };

  const describe = (revision) =>
    `${revision.label}${revision.isCurrent ? " (opens by default)" : ""}${revision.isPending ? " - pending" : ""}`;

  return (
    <>
      <div className="input-group">
        <select id={id} className="form-select" value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Default (the one Agile opens)</option>
          {(revisions || []).map((revision) => (
            <option key={revision.label} value={revision.label}>{describe(revision)}</option>
          ))}
          {value && !(revisions || []).some((revision) => revision.label === value) && <option value={value}>{value}</option>}
        </select>
        <button type="button" className="btn btn-outline-secondary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : revisions ? "Reload" : "Load versions"}
        </button>
      </div>
      {error && <div className="text-danger small mt-1" role="alert">{error}</div>}
    </>
  );
}
